import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CalendarDays,
  Inbox,
  Loader2,
  LogOut,
  MapPin,
  Phone,
} from "lucide-react";
import axios from "axios";
import Monogram from "../components/Monogram";
import { useAuth } from "../context/AuthContext";
import { EASE } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/enquiries`, { withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => setEnquiries([]));
  }, []);

  const signOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-ink text-ivory font-body">
      <header className="border-b border-gold/10 bg-ink-soft/60 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Monogram className="w-9 h-9 text-gold" />
            <div className="leading-none">
              <p className="font-display text-lg tracking-[0.14em] text-ivory">
                BOOKING ENQUIRIES
              </p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mt-1">
                Mohit Studio · Admin
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-9 h-9 rounded-full border border-gold/40"
              />
            )}
            <div className="hidden sm:block text-right leading-tight">
              <p className="text-sm text-ivory">{user?.name}</p>
              <p className="text-[11px] text-ivory-mute">{user?.email}</p>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={signOut}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-[11px] uppercase tracking-[0.18em] text-ivory-dim hover:border-gold hover:text-gold transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl text-ivory">
            Enquiries{" "}
            <span className="text-gold" data-testid="admin-total-count">
              {enquiries ? enquiries.length : "…"}
            </span>
          </h1>
          <Link
            data-testid="admin-back-to-site"
            to="/"
            className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ivory-dim hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to website
          </Link>
        </div>

        {enquiries === null ? (
          <div data-testid="admin-loading" className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-gold" />
          </div>
        ) : enquiries.length === 0 ? (
          <div
            data-testid="admin-empty-state"
            className="border border-white/[0.07] bg-ink-soft/50 py-20 flex flex-col items-center gap-4 text-center px-6"
          >
            <Inbox className="w-10 h-10 text-gold/50" strokeWidth={1.4} />
            <p className="font-display text-2xl text-ivory-dim">No enquiries yet</p>
            <p className="text-sm text-ivory-mute">
              New bookings from the website form will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-enquiry-list">
            {enquiries.map((enq, i) => (
              <motion.div
                key={enq.id}
                data-testid={`admin-enquiry-item-${i + 1}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.35), ease: EASE }}
                className="border border-white/[0.07] bg-ink-soft/50 p-5 sm:p-6 hover:border-gold/30 transition-colors"
              >
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="font-display text-2xl text-ivory">{enq.name}</p>
                    <a
                      href={`tel:${enq.phone.replace(/\s/g, "")}`}
                      data-testid={`admin-enquiry-phone-${i + 1}`}
                      className="flex items-center gap-2 text-gold text-sm mt-1"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {enq.phone}
                    </a>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {enq.event_type && (
                      <span className="text-[10px] uppercase tracking-[0.18em] bg-gold/15 text-gold px-3 py-1.5">
                        {enq.event_type}
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-[0.18em] border border-white/10 text-ivory-mute px-3 py-1.5">
                      Received{" "}
                      {new Date(enq.created_at).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm text-ivory-dim">
                  {enq.wedding_date && (
                    <p className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-gold shrink-0" />
                      {enq.wedding_date}
                    </p>
                  )}
                  {enq.venue_city && (
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gold shrink-0" />
                      {enq.venue_city}
                    </p>
                  )}
                </div>
                {enq.message && (
                  <p className="mt-4 text-sm text-ivory-dim/90 border-l-2 border-gold/30 pl-4 italic">
                    “{enq.message}”
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
